/**
 * Product Title Updater
 * Dynamically updates product title when variant changes
 * Compatible with Shopify native variant system
 */

(function() {
  'use strict';

  // Configuration
  const TITLE_SELECTOR = '[data-product-title]';
  const TITLE_FORMAT = (baseTitle, variantTitle) => `${baseTitle} – ${variantTitle}`;
  
  // State
  let variantsData = [];
  let titleElement = null;
  let baseTitle = '';

  /**
   * Initialize the title updater
   */
  function init() {
    // Find title element
    titleElement = document.querySelector(TITLE_SELECTOR);
    if (!titleElement) {
      console.warn('Product title element not found');
      return;
    }

    // Get base title
    baseTitle = titleElement.getAttribute('data-product-title-base');
    if (!baseTitle) {
      console.warn('Base title not found');
      return;
    }

    // Load variants data
    variantsData = getVariantsData();
    if (variantsData.length === 0) {
      console.warn('No variants data found');
      return;
    }

    // Setup event listeners
    setupEventListeners();

    // Set initial title based on selected variant (if any)
    setInitialTitle();
  }

  /**
   * Set initial title based on currently selected variant
   */
  function setInitialTitle() {
    // Try to get the currently selected variant ID
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      const variantId = parseInt(variantInput.value, 10);
      const variant = variantsData.find(v => v.id === variantId);
      if (variant && variant.title && variant.title !== 'Default Title') {
        updateTitle(variant.title);
      }
    }
  }

  /**
   * Get variants data from JSON script tag
   */
  function getVariantsData() {
    const scripts = document.querySelectorAll('script[type="application/json"]');
    
    for (let script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        // Check if this looks like variants data (array with id and title)
        if (Array.isArray(data) && data.length > 0 && data[0].id && data[0].title) {
          return data;
        }
      } catch (e) {
        // Continue to next script
      }
    }
    
    return [];
  }

  /**
   * Setup all event listeners
   */
  function setupEventListeners() {
    // 1. Listen for custom variant:change events (primary)
    document.addEventListener('variant:change', handleVariantChangeEvent);
    document.addEventListener('variant:changed', handleVariantChangeEvent);

    // 2. Listen for form input changes (fallback)
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', handleVariantInputChange);
    }

    // 3. Listen for radio button and select changes (additional fallback)
    setupOptionChangeListeners();
  }

  /**
   * Handle custom variant:change events
   */
  function handleVariantChangeEvent(event) {
    const variant = event.detail?.variant;
    if (variant && variant.title) {
      updateTitle(variant.title);
    }
  }

  /**
   * Handle variant input field changes
   */
  function handleVariantInputChange(event) {
    const variantId = parseInt(event.target.value, 10);
    if (!variantId) return;

    const variant = variantsData.find(v => v.id === variantId);
    if (variant) {
      updateTitle(variant.title);
    }
  }

  /**
   * Setup listeners for option changes (radio buttons, selects)
   */
  function setupOptionChangeListeners() {
    // Find all variant option inputs - including custom names like "Combo"
    const optionInputs = document.querySelectorAll(
      'input[type="radio"], select[name*="option"]'
    );

    optionInputs.forEach(input => {
      input.addEventListener('change', handleOptionChange);
    });
  }

  /**
   * Handle option input changes
   */
  function handleOptionChange() {
    // Small delay to ensure all options are updated
    setTimeout(() => {
      const selectedOptions = getSelectedOptions();
      const variant = findVariantByOptions(selectedOptions);
      
      if (variant) {
        updateTitle(variant.title);
      }
    }, 50);
  }

  /**
   * Get currently selected options
   */
  function getSelectedOptions() {
    const options = [];
    
    // First, try to find the variant ID input and match it directly
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      const variantId = parseInt(variantInput.value, 10);
      const variant = variantsData.find(v => v.id === variantId);
      if (variant) {
        // Return the variant's options
        for (let i = 1; i <= 3; i++) {
          const optionValue = variant[`option${i}`];
          if (optionValue) {
            options.push(optionValue);
          }
        }
        return options;
      }
    }
    
    // Fallback: Try to find option inputs in order (option1, option2, option3)
    for (let i = 1; i <= 3; i++) {
      // Check radio buttons with standard naming
      const radio = document.querySelector(`input[type="radio"][name*="option${i}"]:checked, input[type="radio"][name="options[${i}]"]:checked`);
      if (radio) {
        options.push(radio.value);
        continue;
      }
      
      // Check select dropdowns
      const select = document.querySelector(`select[name*="option${i}"], select[name="options[${i}]"]`);
      if (select && select.value) {
        options.push(select.value);
        continue;
      }
      
      // Check for custom naming (like "Combo")
      const customRadio = document.querySelector(`input[type="radio"]:checked`);
      if (customRadio && customRadio.value) {
        options.push(customRadio.value);
        break;
      }
      
      // If no option found, break (assuming no more options)
      break;
    }
    
    return options;
  }

  /**
   * Find variant by selected options
   */
  function findVariantByOptions(selectedOptions) {
    return variantsData.find(variant => {
      // Match all selected options with variant options
      return selectedOptions.every((option, index) => {
        const variantOption = variant[`option${index + 1}`];
        return variantOption === option;
      });
    });
  }

  /**
   * Update the product title
   */
  function updateTitle(variantTitle) {
    if (!titleElement || !baseTitle) return;

    // Skip if variant title is "Default Title" or empty
    if (!variantTitle || variantTitle === 'Default Title') {
      titleElement.textContent = baseTitle;
      return;
    }

    // Clean the variant title - remove leading +, -, or extra spaces
    let cleanedTitle = variantTitle
      .replace(/^[\+\-]\s*/, '') // Remove leading + or - with optional space
      .trim(); // Remove extra whitespace

    // Convert to title case (capitalize first letter of each word, lowercase the rest)
    if (cleanedTitle) {
      cleanedTitle = cleanedTitle
        .toLowerCase() // Convert all to lowercase first
        .split(' ') // Split into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
        .join(' '); // Join back together
      
      titleElement.textContent = TITLE_FORMAT(baseTitle, cleanedTitle);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
